// this is a workaround to fix the issue with the heading extension https://github.com/remirror/remirror/issues/2179

import {
  NodeExtension,
  type ApplySchemaAttributes,
  type NodeExtensionSpec,
  type KeyBindings,
  type KeyBindingCommandFunction,
  type CommandFunction,
  type PrimitiveSelection,
} from '@remirror/core';
import { setBlockType as pmSetBlockType } from '@remirror/pm/commands';
import { textblockTypeInputRule } from '@remirror/pm/inputrules';

/**
 * CustomHeadingExtension
 *
 * A minimal, robust replacement for Remirror's built-in HeadingExtension.
 *
 * Features
 * - Defines heading nodes <h1>…<h6>.
 * - Supports Markdown-style input rules: "# " → H1, "## " → H2, … up to "###### " → H6.
 * - Provides keyboard shortcuts: Mod-Alt-1..6 (Cmd on macOS, Ctrl on Windows/Linux).
 */

export class CustomHeadingExtension extends NodeExtension {
  get name() {
    return 'customHeading' as const;
  }

  /**
   * Define the schema for heading nodes.
   * - Allows `inline*` content.
   * - Belongs to the `block` group.
   * - Defines attributes including `level` (default: 1).
   */
  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeExtensionSpec = {}
  ): NodeExtensionSpec {
    return {
      content: 'inline*',
      group: 'block',
      defining: true,
      attrs: { ...extra.defaults(), level: { default: 1 } },

      // Map between DOM <h1>..<h6> and this node type.
      parseDOM: [
        { tag: 'h1', getAttrs: (node) => ({ ...extra.parse(node), level: 1 }) },
        { tag: 'h2', getAttrs: (node) => ({ ...extra.parse(node), level: 2 }) },
        { tag: 'h3', getAttrs: (node) => ({ ...extra.parse(node), level: 3 }) },
        { tag: 'h4', getAttrs: (node) => ({ ...extra.parse(node), level: 4 }) },
        { tag: 'h5', getAttrs: (node) => ({ ...extra.parse(node), level: 5 }) },
        { tag: 'h6', getAttrs: (node) => ({ ...extra.parse(node), level: 6 }) },
      ],

      // Render to <h1>..<h6> depending on the level attribute.
      toDOM: (node) => {
        const lvl = Math.max(1, Math.min(6, node.attrs.level ?? 1));
        return [`h${lvl}`, extra.dom(node), 0];
      },

      ...override,
    };
  }

  /**
   * Markdown-style input rules.
   * - Typing `# ` turns the block into an H1.
   * - Typing `## ` turns it into an H2.
   * - …up to `###### ` for H6.
   */
  createInputRules() {
    return [
      textblockTypeInputRule(/^(#{1,6})\s$/, this.type, (match) => ({
        level: match[1].length,
      })),
    ];
  }

  /**
   * Remirror commands exposed to useCommands() hook.
   * - setBlockType: Changes the current block to a specific node type.
   */
  createCommands() {
    return {
      setBlockType:
        (props: {
          type: string;
          attrs?: Record<string, unknown>;
          selection?: PrimitiveSelection;
        }): CommandFunction =>
        ({ state, dispatch }) => {
          const { type, attrs } = props;

          if (type === 'paragraph') {
            const paragraphType = state.schema.nodes.paragraph;
            if (!paragraphType) return false;
            return pmSetBlockType(paragraphType, attrs)(state, dispatch);
          }

          if (type === 'customHeading') {
            return pmSetBlockType(this.type, attrs)(state, dispatch);
          }

          // Fallback for other types
          const nodeType = state.schema.nodes[type];
          if (!nodeType) return false;
          return pmSetBlockType(nodeType, attrs)(state, dispatch);
        },
    };
  }

  /**
   * Keyboard shortcuts.
   * - Cmd/Ctrl + Alt + 1 → H1
   * - Cmd/Ctrl + Alt + 2 → H2
   * - … up to 6.
   *
   * Uses ProseMirror's `setBlockType` directly instead of relying on Remirror commands,
   * which avoids the `CommandsExtension` dependency.
   */
  createKeymap(): KeyBindings {
    const set =
      (level: number): KeyBindingCommandFunction =>
      ({ state, dispatch }) => {
        return pmSetBlockType(this.type, { level })(state, dispatch);
      };

    return {
      'Mod-Alt-1': set(1),
      'Mod-Alt-2': set(2),
      'Mod-Alt-3': set(3),
      'Mod-Alt-4': set(4),
      'Mod-Alt-5': set(5),
      'Mod-Alt-6': set(6),
    };
  }
}
